import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query"
import {
	ErrorResponse,
	SuccessPageResponse,
	SuccessResponse,
	useHttpClient,
} from "api/api"
import {
	AdminUser,
	AdminUserInvitePayload,
	AdminUserUpdatePayload,
	LegalUrls,
	Organization,
	PhoneCheck,
	Role,
	Team,
	User,
} from "interface/user"
import { useAuth } from "modules/auth/Auth"
import { useEffect, useMemo } from "react"
import { useParams } from "react-router"
import { sortBy } from "utils"

export const queryKeys = {
	userRole: "user-role",
	userGitbookToken: "user-gitbook-token",
	users: "users",
	setPassword: "set-password",
	usersLegalAccept: "users-legal-accept",
	usersLegalUrls: "users-legal-urls",
	usersCheckPhone: "users-check-phone",
	adminUsersList: "admin-users-list",
	adminUser: "admin-user",
	adminUserUpdate: "admin-user-update",
	adminUserInvite: "admin-user-invite",
	adminUserInviteResend: "admin-user-invite-resend",
	adminUserInviteDelete: "admin-user-delete",
}

export const useUserRole = () => {
	const { httpClient } = useHttpClient()
	const { isLoaded, isSignedIn } = useAuth()
	const queryClient = useQueryClient()
	const queryKey = useMemo(() => [queryKeys.userRole], [])

	useEffect(() => {
		if (!isSignedIn) {
			queryClient.removeQueries({ queryKey })
		}
	}, [isSignedIn, queryClient, queryKey])

	return useQuery({
		queryKey,
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<User>>("/api/v1/users/role", {
				signal,
			})
		},
		enabled:
			(isLoaded && isSignedIn) ||
			import.meta.env.MODE === "test" ||
			import.meta.env.STORYBOOK === "true",
		staleTime: 3_600_000, // 1 hour
		select({ data: { data } }) {
			return {
				...data,
				isProvider: data.role === "Provider",
				isProviderAnon: data.role === "Provider-anon",
				isPatient: data.role === "Patient",
				isFieldSupportAgent: data.role === "Field-support-agent",
				isAdmin: data.role === "Administrator",
			}
		},
	})
}

export const useGitbookToken = (enabled = false) => {
	const { httpClient } = useHttpClient()
	const role = useUserRole()

	return useQuery({
		queryKey: [queryKeys.userGitbookToken, role.data?.id],
		async queryFn({ signal }) {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<{ token: string }>>(
				"/api/v1/users/gitbook-token",
				{
					signal,
				},
			)

			return data ?? null
		},
		enabled: enabled && !!role.data?.id,
		refetchInterval: 50 * 60 * 1000, // Refetch every 50 minutes
		refetchIntervalInBackground: true,
		select(data) {
			return data?.token
		},
	})
}

export const useUsers = (role: Role, enabled = true) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		enabled,
		queryKey: [queryKeys.users, role],
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<User[]>>("/api/v1/users", {
				signal,
				params: {
					role,
				},
			})
		},
		staleTime: 3_600_000, // 1 hour
		select({ data }) {
			return data.data.map((user) => ({
				...user,
				isProvider: user.role === "Provider",
				isProviderAnon: user.role === "Provider-anon",
				isPatient: user.role === "Patient",
				isFieldSupportAgent: user.role === "Field-support-agent",
			}))
		},
	})
}

export const useOrgPatients = (orgId?: string) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		enabled: !!orgId,
		queryKey: [queryKeys.users, orgId],
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<User[]>>(
				`/api/v2/organizations/${orgId}/patients`,
				{
					signal,
				},
			)
		},
		select({ data }) {
			return data.data
		},
	})
}

export const useSetPassword = () => {
	const { httpClient } = useHttpClient()
	type Payload = { newPassword: string; invitationId: string }

	return useMutation<unknown, ErrorResponse, Payload>({
		mutationKey: [queryKeys.setPassword],
		mutationFn: (data) => httpClient.post("/api/v1/user-password/set", data),
	})
}

export const useOrganization = (): Organization | undefined => {
	const { scopeId } = useParams()
	const { data } = useUserRole()
	const organization = useMemo(() => {
		const organizations = data?.organizations ?? []
		const organization =
			organizations.find(({ id }) => scopeId === id) ?? organizations[0]
		if (organization) {
			organization.hasSend = organization.communicationPermission === "send"
			organization.hasView = organization.communicationPermission === "view"
		}

		return organization
	}, [data?.organizations, scopeId])

	return organization
}

export const useTeam = (): Team | undefined => {
	const { scopeId } = useParams()
	const { data } = useUserRole()
	const team = useMemo(() => {
		const teams = data?.fieldSupportTeams ?? []
		const team = teams.find(({ id }) => scopeId === id) ?? teams[0]
		return team
	}, [data?.fieldSupportTeams, scopeId])

	return team
}

export const useLegalAccept = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<SuccessResponse<undefined>, ErrorResponse>({
		mutationKey: [queryKeys.usersLegalAccept],
		mutationFn: () => httpClient.patch("/api/v1/users/legal"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.userRole],
			})
		},
	})
}

export const useLegalURLs = (enabled?: boolean) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.usersLegalUrls],
		queryFn({ signal }) {
			return httpClient.get<SuccessResponse<LegalUrls>>("/api/v1/users/legal", {
				signal,
			})
		},
		select({ data }) {
			return data.data
		},
		enabled,
	})
}

export const useCheckPhoneNumber = (userId?: string, enrollmentId?: string) => {
	const { httpClient } = useHttpClient()

	// Could have used useQuery but useMutation provides a straightforward API for the on-demand GET
	return useMutation({
		mutationKey: [queryKeys.usersCheckPhone],
		mutationFn: async (phoneNumber?: string) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<PhoneCheck>>(
				"/api/v1/users/phone-check",
				{ params: { phoneNumber, userId, enrollmentId } },
			)

			return data
		},
	})
}

export const useAdminUsers = ({
	sort,
	query,
	page = "1",
	role,
	internalOnly,
	enabled = true,
}: {
	sort: string[]
	query?: string
	page?: string
	role?: Array<AdminUser["userType"]>
	internalOnly?: boolean
	enabled?: boolean
}) => {
	const { httpClient } = useHttpClient()
	const params = useMemo(
		() => ({
			limit: 20,
			page,
			role,
			sort,
			search: query?.trim() || undefined,
			internalOnly,
		}),
		[page, query, role, sort, internalOnly],
	)

	return useQuery({
		queryKey: [queryKeys.adminUsersList, params],
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessPageResponse<AdminUser>>(
				"/api/v1/admin/users",
				{
					signal,
					params,
				},
			)

			return data
		},
		placeholderData: keepPreviousData,
		enabled,
	})
}

export const useAdminUser = (userId: string) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.adminUser, userId],
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<AdminUser>>(
				`/api/v2/admin/users/${userId}`,
				{
					signal,
				},
			)

			return data
		},
		placeholderData: keepPreviousData,
		select(user) {
			const device = Array.isArray(user.device) ? user.device[0] : user.device

			return {
				...user,
				device,
				careOrganizations: user.careOrganizations?.toSorted(sortBy("name")),
				fieldSupportTeams: user.fieldSupportTeams?.toSorted(sortBy("name")),
			}
		},
	})
}

export const useAdminUserUpdate = (userId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<AdminUser, ErrorResponse, AdminUserUpdatePayload>({
		mutationKey: [queryKeys.adminUserUpdate, userId],
		mutationFn: async (payload) => {
			const {
				data: { data },
			} = await httpClient.patch<SuccessResponse<AdminUser>>(
				`/api/v1/admin/users/${userId}`,
				payload,
			)

			return data
		},
		onSuccess: (updatedUser) => {
			queryClient.setQueryData<AdminUser>(
				[queryKeys.adminUser, userId],
				(prev) => ({ ...prev, ...updatedUser }),
			)

			queryClient.setQueriesData<SuccessPageResponse<AdminUser>["data"]>(
				{ queryKey: [queryKeys.adminUsersList] },
				(prev) => {
					if (!prev) {
						return prev
					}

					return {
						...prev,
						result: prev.result.map((prev) => {
							if (prev.id !== userId) {
								return prev
							}

							return {
								...prev,
								...updatedUser,
							}
						}),
					}
				},
			)
		},
	})
}

export const useAdminUserInvite = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<AdminUser, ErrorResponse, AdminUserInvitePayload>({
		mutationKey: [queryKeys.adminUserInvite],
		mutationFn: async (invitation) => {
			const {
				data: { data },
			} = await httpClient.post<SuccessResponse<AdminUser>>(
				"/api/v1/admin/users/invitation",
				invitation,
			)

			return data
		},
		onSuccess: (user) => {
			queryClient.setQueryData<AdminUser>(
				[queryKeys.adminUser, user.id],
				() => user,
			)

			queryClient.setQueriesData<SuccessPageResponse<AdminUser>["data"]>(
				{ queryKey: [queryKeys.adminUsersList] },
				(prev) => {
					if (!prev) {
						return prev
					}

					return {
						...prev,
						total: prev.total + 1,
						result: [user, ...prev.result].slice(0, 20),
					}
				},
			)
		},
	})
}

export const useAdminUserInviteResend = () => {
	const { httpClient } = useHttpClient()

	return useMutation<void, ErrorResponse, Pick<AdminUser, "email">>({
		mutationKey: [queryKeys.adminUserInviteResend],
		mutationFn: (invitation) =>
			httpClient.post("/api/v1/admin/users/reset-password-email", invitation),
	})
}

export const useAdminUserDelete = () => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: [queryKeys.adminUserInviteDelete],
		mutationFn: async (id: AdminUser["id"]) =>
			httpClient.delete(`/api/v2/admin/users/${id}`),
		onSuccess: (_, id) => {
			queryClient.setQueriesData<SuccessPageResponse<AdminUser>["data"]>(
				{ queryKey: [queryKeys.adminUsersList] },
				(prev) => {
					if (!prev) {
						return prev
					}

					return {
						...prev,
						result: prev.result.filter((user) => user.id !== id),
					}
				},
			)
		},
	})
}
